import { FC } from "react";
import { StepsProps } from "../../types";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useStep4 from "./useStep4";
import {
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import {
  ACCOUNT_ID,
  ACH_ACCOUNT,
  ACH_ACCOUNT_NAME,
  ACH_BANK_NAME,
  ACH_ROUTING,
  AUTO_BILLING_ENROLL,
  AUTO_BILLING_LA,
  AUTO_BILLING_LA_END_DATE,
  AUTO_BILLING_LA_START_DATE,
  AUTO_BILLING_START_DATE,
  E_ACCOUNT_EMAIL,
  E_ACCOUNT_NAME,
  E_ACCOUNT_NO,
  E_ACCOUNT_PHONE,
  E_COM_SIGNUP,
  E_COM_START_DATE,
  PAYMENT_TYPE,
  PAYMENT_TYPE_OPTIONS,
  ZELLE_URL,
} from "./consts";

const Step4: FC<StepsProps> = ({ onCancel, isView }) => {
  const {
    form,
    requiredFields,
    isOwnerAccountDtlDetailsLoading,
    userId,
    ownerAccountDtlDetails,
    key,
    isOwnerAccountDtlDetailsRefetching,
  } = useStep4();
  const { errors } = form.formState;

  return isOwnerAccountDtlDetailsLoading ||
    isOwnerAccountDtlDetailsRefetching ? (
    <Loader />
  ) : (
    <FormProvider {...form}>
      <form>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <div>
            <FormSectionHeader>Accounting Info</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              {userId && ownerAccountDtlDetails?.data?.[ACCOUNT_ID] && (
                <FormInput
                  name={ACCOUNT_ID}
                  control={form.control}
                  label="Account ID"
                  placeholder="Please enter"
                  error={errors[ACCOUNT_ID]?.message}
                  isRequired={requiredFields.includes(ACCOUNT_ID)}
                  disabled
                />
              )}
              <FormSelect
                name={PAYMENT_TYPE}
                control={form.control}
                label="Payment Type"
                placeholder="Please Select"
                error={errors[PAYMENT_TYPE]?.message}
                isRequired={requiredFields.includes(PAYMENT_TYPE)}
                options={PAYMENT_TYPE_OPTIONS}
                disabled={isView}
                key={key}
              />
              {form.watch(PAYMENT_TYPE) === "ZELLE" && (
                <FormInput
                  name={ZELLE_URL}
                  control={form.control}
                  label="Zelle Url"
                  placeholder="Please enter"
                  error={errors[ZELLE_URL]?.message}
                  isRequired
                  disabled={isView}
                />
              )}
              {form.watch(PAYMENT_TYPE) === "ACH" && (
                <>
                  <FormInput
                    name={ACH_ROUTING}
                    control={form.control}
                    label="ACH Routing"
                    placeholder="Please enter"
                    error={errors[ACH_ROUTING]?.message}
                    isRequired
                    disabled={isView}
                  />
                  <FormInput
                    name={ACH_ACCOUNT}
                    control={form.control}
                    label="ACH Account"
                    placeholder="Please enter"
                    error={errors[ACH_ACCOUNT]?.message}
                    isRequired
                    disabled={isView}
                  />
                  <FormInput
                    name={ACH_ACCOUNT_NAME}
                    control={form.control}
                    label="ACH Account Name"
                    placeholder="Please enter"
                    error={errors[ACH_ACCOUNT_NAME]?.message}
                    isRequired
                    disabled={isView}
                  />
                  <FormInput
                    name={ACH_BANK_NAME}
                    control={form.control}
                    label="ACH Bank Name"
                    placeholder="Please enter"
                    error={errors[ACH_BANK_NAME]?.message}
                    isRequired
                    disabled={isView}
                  />
                </>
              )}
            </div>
          </div>
          <div className="mt-[40px]">
            <FormSectionHeader>E-Accounting Info</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormInput
                name={E_ACCOUNT_NO}
                control={form.control}
                label="E Account #"
                placeholder="Please enter"
                error={errors[E_ACCOUNT_NO]?.message}
                isRequired={requiredFields.includes(E_ACCOUNT_NO)}
                disabled={isView}
              />
              <FormInput
                name={E_ACCOUNT_NAME}
                control={form.control}
                label="E Account Name"
                placeholder="Please enter"
                error={errors[E_ACCOUNT_NAME]?.message}
                isRequired={requiredFields.includes(E_ACCOUNT_NAME)}
                disabled={isView}
              />
              <FormInput
                name={E_ACCOUNT_EMAIL}
                control={form.control}
                label="E Account Email"
                placeholder="Please enter"
                error={errors[E_ACCOUNT_EMAIL]?.message}
                isRequired={requiredFields.includes(E_ACCOUNT_EMAIL)}
                disabled={isView}
              />
              <FormPhoneInput
                name={E_ACCOUNT_PHONE}
                control={form.control}
                label="E Account Phone"
                placeholder="Please enter"
                error={errors[E_ACCOUNT_PHONE]?.message}
                isRequired={requiredFields.includes(E_ACCOUNT_PHONE)}
                disabled={isView}
              />
            </div>
          </div>
          <div className="mt-[40px]">
            <FormSectionHeader>Account Preference</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormCheckbox
                name={E_COM_SIGNUP}
                control={form.control}
                label="E-Com Signup"
                error={errors[E_ACCOUNT_NO]?.message}
                isRequired={requiredFields.includes(E_ACCOUNT_NO)}
                disabled={isView}
              />
              {form.watch(E_COM_SIGNUP) && (
                <FormDatePicker
                  name={E_COM_START_DATE}
                  control={form.control}
                  label="E-Com Start Date"
                  placeholder="Please select"
                  error={errors[E_COM_START_DATE]?.message}
                  isRequired={true}
                  disabled={isView}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] my-[20px]">
              <FormCheckbox
                name={AUTO_BILLING_ENROLL}
                control={form.control}
                label="Auto Billing Enrolled"
                error={errors[AUTO_BILLING_ENROLL]?.message}
                isRequired={requiredFields.includes(AUTO_BILLING_ENROLL)}
                disabled={isView}
              />
              {form.watch(AUTO_BILLING_ENROLL) && (
                <FormDatePicker
                  name={AUTO_BILLING_START_DATE}
                  control={form.control}
                  label="Auto Billing Enrolled Date"
                  placeholder="Please select"
                  error={errors[AUTO_BILLING_START_DATE]?.message}
                  isRequired={true}
                  disabled={isView}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] my-[20px]">
              <FormCheckbox
                name={AUTO_BILLING_LA}
                control={form.control}
                label="Auto Billing For Lease Admin"
                error={errors[AUTO_BILLING_LA]?.message}
                isRequired={requiredFields.includes(AUTO_BILLING_LA)}
                disabled={isView}
              />
              {form.watch(AUTO_BILLING_LA) && (
                <>
                  <FormDatePicker
                    name={AUTO_BILLING_LA_START_DATE}
                    control={form.control}
                    label="Start Date"
                    placeholder="Please select"
                    error={errors[AUTO_BILLING_LA_START_DATE]?.message}
                    isRequired={true}
                    disabled={isView}
                  />
                  <FormDatePicker
                    name={AUTO_BILLING_LA_END_DATE}
                    control={form.control}
                    label="End Date"
                    placeholder="Please select"
                    error={errors[AUTO_BILLING_LA_END_DATE]?.message}
                    isRequired={requiredFields.includes(
                      AUTO_BILLING_LA_END_DATE
                    )}
                    disabled={isView}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-between items-center  py-3">
          <div className="flex flex-row justify-end gap-3  py-3">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step4;
